import type { TError } from '@onesource/schemas';
import { useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

interface ReactionUser {
  id: string;
  name: string;
  avatar: string | null;
}

interface ReactionSummary {
  emoji: string;
  count: number;
}

interface ReactionDetail {
  emoji: string;
  user: ReactionUser;
}

interface ReactionsResponse {
  summary: ReactionSummary[];
  reactions: ReactionDetail[];
}

/**
 * Query key for fetching reaction details
 */
export const getReactionsQueryKey = (targetType: string, targetId: string) => [
  ENTITY,
  'reactions',
  targetType,
  targetId,
];

/**
 * Query key for fetching reaction details - hook version
 */
export const useGetReactionsQueryKey = (targetType: string, targetId: string) =>
  getReactionsQueryKey(targetType, targetId);

/**
 * Hook to fetch reaction details for a post or comment
 * Returns summary (emoji counts) and list of users who reacted
 *
 * @param targetType - 'post' or 'comment'
 * @param targetId - UUID of the post or comment
 * @param enabled - Whether to enable the query (default: false, fetch on demand)
 */
export const useGetReactions = (targetType: 'post' | 'comment', targetId: string, enabled = false) => {
  return useQuery<ReactionsResponse, TError>({
    queryKey: useGetReactionsQueryKey(targetType, targetId),
    queryFn: async () => {
      const res = await axios.get<ReactionsResponse>(`/community-hub/reactions/${targetType}/${targetId}`);
      return res.data;
    },
    enabled: enabled && !!targetId,
    staleTime: 30 * 1000, // 30 seconds
  });
};
